import React, { useEffect, useMemo } from "react";
import { useHotel } from "@/hooks/useHotel";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

export enum FeedbacksType {
  REQUESTS = "REQUESTS",
  COMMUNICATIONS = "COMMUNICATIONS",
  RESERVATIONS = "RESERVATIONS",
}

const feedbackTabsListClassName =
  "h-9 items-stretch gap-1 bg-secondary-badge p-1 text-black shadow-xs";
const feedbackTabsTriggerClassName =
  "h-full rounded-md border border-transparent px-4 py-1 text-xs font-medium shadow-none data-[state=active]:border-custom-outline data-[state=active]:bg-royal-glow-5 data-[state=active]:text-black data-[state=active]:shadow-none";

export const FeedbacksNavigation = () => {
  const { isLoading, error } = useHotel();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();

  const defaultTab = capabilities.canUseRequestsSystem
    ? FeedbacksType.REQUESTS
    : FeedbacksType.COMMUNICATIONS;
  const activeTab = useMemo(() => {
    const tab = searchParams.get("tab") as FeedbacksType | null;
    if (!tab) {
      return defaultTab;
    }

    if (!capabilities.canUseRequestsSystem && tab === FeedbacksType.REQUESTS) {
      return defaultTab;
    }

    return tab;
  }, [capabilities.canUseRequestsSystem, defaultTab, searchParams]);

  useEffect(() => {
    if (
      !capabilities.canUseRequestsSystem &&
      searchParams.get("tab") === FeedbacksType.REQUESTS
    ) {
      setSearchParams({ tab: defaultTab });
    }
  }, [
    capabilities.canUseRequestsSystem,
    defaultTab,
    searchParams,
    setSearchParams,
  ]);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (isLoading) {
    return <div>{t("feedbacks.loading")}</div>;
  }

  if (error) {
    return (
      <div className="text-destructive py-4 text-center">
        {t("feedbacks.errorHotelLoading")}: {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-12">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <div className="mb-6 flex items-center justify-center">
          <TabsList className={feedbackTabsListClassName}>
            {capabilities.canUseRequestsSystem ? (
              <TabsTrigger
                value={FeedbacksType.REQUESTS}
                className={feedbackTabsTriggerClassName}
              >
                {t("feedbacks.requests", "Requests")}
              </TabsTrigger>
            ) : null}

            <TabsTrigger
              value={FeedbacksType.COMMUNICATIONS}
              className={feedbackTabsTriggerClassName}
            >
              {t("feedbacks.communications", "Communications")}
            </TabsTrigger>

            <TabsTrigger
              value={FeedbacksType.RESERVATIONS}
              className={feedbackTabsTriggerClassName}
            >
              {t("feedbacks.reservations", "Reservations")}
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>
    </div>
  );
};
